import Sentry from '@/sentry'
import { deepClone } from 'valtio/utils'

import { triggerToast } from '@/shared/ui/toast'
import * as logger from '@/utils/logger'

import type { Column, ColumnPatch } from '~shared/rundown/column'
import type { Cell } from '~shared/types'
import { RichtextCellContent, SelectCellContent } from '~shared/types/Cell'

import {
  getCell,
  waitForCellVersion,
} from '@/features/rundown/data/cells.store'

import { upsertCell } from '../cell/cell.api'
import { type CellKey } from '../cell/cell.schema'
import { parseCellKey } from '../cell/cell.schema'
import { updateColumn } from '../column/column.api'
import { getRundown, getRundownId } from '../data/rundown.store'

import { getLastSeenUpdatedAt } from './grid.save.helpers'
import { gridState, gridStateActions } from './grid.store'

export type SaveKey = `cell:${CellKey}` | `column:${Column['id']}`

export type SavePayload =
  | {
      kind: 'cellText'
      cellKey: CellKey
      content: RichtextCellContent
    }
  | {
      kind: 'cellSelect'
      cellKey: CellKey
      content: SelectCellContent
    }
  | {
      kind: 'columnOptions'
      columnId: string
      content: Required<Pick<ColumnPatch, 'options'>>
    }

export type SaveEntry<P extends SavePayload = SavePayload> =
  | {
      status: 'pending'
      entryId: string
      payload: P
      startedAt: number
    }
  | {
      /**
       * Server accepted the save, but the listener has not delivered that
       * version yet. Keeps overlaying until it does.
       */
      status: 'confirmed'
      entryId: string
      payload: P
      startedAt: number
      /** Server `updatedAt` of the saved version, as ms epoch */
      confirmedUpdatedAt: number
    }
  | {
      status: 'error'
      entryId: string
      payload: P
      startedAt: number
      error: Error
      errorKind: 'network' | 'conflict' // TODO: `conflict` is unused for now
    }

export type CellSaveEntry = SaveEntry<
  Extract<SavePayload, { kind: 'cellText' | 'cellSelect' }>
>

export type ColumnSaveEntry = SaveEntry<
  Extract<SavePayload, { kind: 'columnOptions' }>
>

export const saveKeys = {
  cell: (cellKey: CellKey) => `cell:${cellKey}` as const,
  column: (columnId: string) => `column:${columnId}` as const,
}

/**
 * How long a confirmed save waits for the listener before it is dropped.
 */
const LISTENER_SYNC_LIMIT_MS = 60_000

/**
 * Listener lag above this is reported to Sentry. Normally the snapshot
 * arrives before or within a second of the save response.
 */
const LISTENER_LAG_REPORT_MS = 5_000

/**
 * Last version this client saved, per cell, as ms epoch. Lets the UI tell its
 * own updates apart from external ones, even when the listener is late.
 */
const ownCellVersions = new Map<CellKey, number>()

/**
 * Whether a cell version delivered by the listener came from this client.
 *
 * A pending save counts as ours: its version is not known until the response,
 * and the snapshot often arrives first.
 */
export function isOwnCellUpdate(cellKey: CellKey, updatedAt: Date): boolean {
  if (gridState.saveRegistry[saveKeys.cell(cellKey)]?.status === 'pending') {
    return true
  }

  return ownCellVersions.get(cellKey) === updatedAt.getTime()
}

/**
 * Keeps the newest version, as responses can arrive out of order.
 */
function recordOwnCellVersion(cellKey: CellKey, updatedAtMs: number) {
  const current = ownCellVersions.get(cellKey) ?? 0
  if (updatedAtMs > current) ownCellVersions.set(cellKey, updatedAtMs)
}

/**
 * Encapsulate and export actions so they don't pollute the store file.
 */
export const gridSaveActions = {
  commit: (saveKey: SaveKey, payload: SavePayload) => {
    // Blur can fire while the rundown is being torn down. Nothing to save to.
    const rundownId = getRundown()?.id
    if (!rundownId) {
      logger.warn('[grid.save] No rundown loaded, dropping save:', saveKey)
      return
    }

    const entryId = crypto.randomUUID()

    const entryStartedAt = Date.now()

    // Read before the new entry replaces it: a confirmed previous save is a
    // version this client has seen, even if the listener has not caught up.
    const lastSeenUpdatedAt =
      payload.kind === 'columnOptions'
        ? undefined
        : getLastSeenUpdatedAt(
            getCell(payload.cellKey)?.updatedAt,
            gridState.saveRegistry[saveKey],
          )

    const isCurrent = () => gridState.saveRegistry[saveKey]?.entryId === entryId

    // Register request
    gridState.saveRegistry[saveKey] = {
      status: 'pending',
      entryId: entryId,
      startedAt: entryStartedAt,
      payload: payload,
    }

    // Save in background
    persistPayload(payload, lastSeenUpdatedAt)
      .then(
        async (savedCell) => {
          // Invalid dates are converted to `null` by `cellFromSerialized`
          const confirmedUpdatedAt = savedCell?.updatedAt?.getTime() ?? null

          // Record even if superseded, the listener still delivers this version
          if (payload.kind !== 'columnOptions' && confirmedUpdatedAt !== null) {
            recordOwnCellVersion(payload.cellKey, confirmedUpdatedAt)
          }

          // Guard: Only continue if not superseded by other request
          if (!isCurrent()) return

          // Column saves have no version to wait for
          if (payload.kind === 'columnOptions') {
            delete gridState.saveRegistry[saveKey]
            return
          }

          if (confirmedUpdatedAt === null) {
            logger.warn('[grid.save] Response without updatedAt:', saveKey)
            delete gridState.saveRegistry[saveKey]
            return
          }

          // Keep overlaying until the listener delivers this version
          gridState.saveRegistry[saveKey] = {
            status: 'confirmed',
            entryId: entryId,
            startedAt: entryStartedAt,
            payload: payload,
            confirmedUpdatedAt,
          }

          const confirmedAt = Date.now()
          const caughtUp = await waitForCellVersion(
            payload.cellKey,
            confirmedUpdatedAt,
            LISTENER_SYNC_LIMIT_MS,
          )
          const lagMs = Date.now() - confirmedAt

          // Leaving the rundown clears the store, which is not lag
          const isSameRundown = getRundown()?.id === rundownId

          if (isSameRundown && (!caughtUp || lagMs > LISTENER_LAG_REPORT_MS)) {
            logger.warn('[grid.save] Listener lag:', saveKey, lagMs, caughtUp)
            Sentry.captureMessage('Cell listener lag', {
              level: 'warning',
              tags: { caughtUp },
              extra: { saveKey, lagMs },
            })
          }

          if (!isCurrent()) return

          delete gridState.saveRegistry[saveKey]
        },
        (err: unknown) => {
          logger.error('[grid.save] Commit failed:', saveKey, err)

          // Guard: Only continue if not superseded by other request
          if (!isCurrent()) return

          // TODO: Errors never expire and will overlay any incoming content updates. The toast suggests refresh the page, but that will lose the failed changes.
          // Update registry
          gridState.saveRegistry[saveKey] = {
            status: 'error',
            entryId: entryId,
            startedAt: entryStartedAt,
            errorKind: 'network',
            payload: payload,
            error: err instanceof Error ? err : new Error('Save failed'),
          }

          triggerToast({
            type: 'fail',
            title: `Save failed.`,
            body: 'Please try again or copy your changes and refresh the page.',
          })
        },
      )
      .catch((err: unknown) => {
        // Only reached if the success handler itself throws
        logger.error('[grid.save] Post-save handling failed:', saveKey, err)
      })
  },

  /**
   * Retry save for failed cell
   */
  retry: (saveKey: SaveKey) => {
    logger.info('[grid.save]: retry')

    const saveEntry = gridState.saveRegistry[saveKey]

    // Guard: Only retry errored entries
    if (!saveEntry || saveEntry?.status !== 'error') return

    const retryEntry = deepClone(saveEntry)

    return gridSaveActions.commit(saveKey, retryEntry.payload)
  },

  /**
   * Abandon state for failed cell
   *
   * @warning Destructive, should be confirmed by user
   */
  abandon: (saveKey: SaveKey, cellKey: CellKey) => {
    logger.info('[grid.save]: abandon')

    delete gridState.saveRegistry[saveKey]

    // If abandoning active editor, discard session
    if (gridState.editingCell === cellKey) {
      gridStateActions.editor.discard()
    }
  },
}

/**
 * @returns The saved cell, or `null` for column saves
 */
function persistPayload(
  payload: SavePayload,
  lastSeenUpdatedAt: Date | undefined,
): Promise<Cell | null> {
  switch (payload.kind) {
    case 'cellText': {
      const cell = getCell(payload.cellKey)
      const { cueId, columnId } = parseCellKey(payload.cellKey)

      return upsertCell(
        cell,
        columnId,
        cueId,
        { content: payload.content },
        { lastSeenUpdatedAt },
      )
    }

    case 'cellSelect': {
      const cell = getCell(payload.cellKey)
      const { cueId, columnId } = parseCellKey(payload.cellKey)

      return upsertCell(
        cell,
        columnId,
        cueId,
        { content: payload.content },
        { lastSeenUpdatedAt },
      )
    }

    case 'columnOptions':
      return updateColumn(
        {
          rundownId: getRundownId(),
          columnId: payload.columnId,
          payload: payload.content,
        },
        { toast: false },
      ).then(() => null)

    default: {
      const _exhaustive: never = payload
      return _exhaustive
    }
  }
}
